import { HStack } from '@cad-arc';
import React from 'react';
import { Panel } from '../panels/panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { ContactListLeftPane } from './contact-list-left-pane';
import {
    ContactListAction,
    ContactListState,
    ContactListPanelState,
    ContactListItem,
} from './types';
import { ContactListRightPane } from './contact-list-right-pane';
import { keyBy, omitBy } from 'lodash';
import { PanelTitle } from '../panels/panel-title';

function ContactListPanelHeader(): React.ReactElement {
    return <PanelTitle>Contact List</PanelTitle>;
}

function contactListPanelReducer(
    state: ContactListState,
    action: ContactListAction
): ContactListState {
    switch (action.type) {
        case 'SET_CONTACTS':
            return {
                ...state,
                contacts: keyBy(action.payload, 'id'),
            };
        case 'SET_CONTACT':
            return {
                ...state,
                contacts: {
                    ...state.contacts,
                    [action.payload.id]: action.payload,
                },
            };
        case 'SET_PANEL_STATE': {
            return {
                ...state,
                panel: action.payload,
            };
        }
        case 'REMOVE_CONTACT':
            return {
                ...state,
                contacts: omitBy(state.contacts, (contact) => {
                    return contact.id === action.payload;
                }),
            };
        default:
            return state;
    }
}

export function ContactListPanelBody(): React.ReactElement {
    const [contactListState, dispatch] = React.useReducer(contactListPanelReducer, {
        contacts: [],
        panel: {
            formMode: 'HIDDEN',
            selectedContactId: undefined,
        },
    });
    const setPanelState = React.useCallback(
        (panelState: ContactListPanelState) => {
            dispatch({
                type: 'SET_PANEL_STATE',
                payload: panelState,
            });
        },
        [dispatch]
    );
    const setContacts = React.useCallback(
        (contacts: ContactListItem[]) => {
            dispatch({
                type: 'SET_CONTACTS',
                payload: contacts,
            });
        },
        [dispatch]
    );
    const setContact = React.useCallback(
        (contact: ContactListItem) => {
            dispatch({
                type: 'SET_CONTACT',
                payload: contact,
            });
        },
        [dispatch]
    );
    const removeContact = React.useCallback(
        (id: number) => {
            dispatch({
                type: 'REMOVE_CONTACT',
                payload: id,
            });
        },
        [dispatch]
    );
    const contacts = React.useMemo(() => {
        return Object.values(contactListState.contacts);
    }, [contactListState.contacts]);
    return (
        <HStack style={{ gap: '0px' }} flexGrow={1} overflow="hidden" borderRadius="inherit" bg="surface.canvas">
            <ContactListLeftPane
                contacts={contacts}
                panelState={contactListState.panel}
                setContacts={setContacts}
                setPanelState={setPanelState}
            />
            {contactListState.panel.formMode === 'HIDDEN' ? (
                <></>
            ) : (
                <ContactListRightPane
                    key={contactListState.panel.selectedContactId}
                    panelState={contactListState.panel}
                    setPanelState={setPanelState}
                    setContact={setContact}
                    removeContact={removeContact}
                />
            )}
        </HStack>
    );
}

export function ContactListPanel({ panel }: { panel: WorkspacePanel }): React.ReactElement {
    return (
        <Panel
            panel={panel}
            header={<ContactListPanelHeader />}
            body={<ContactListPanelBody />}
        />
    );
}
